"use client";
import React, { useState, useRef, useEffect } from "react";
import { STATUS_OPTIONS } from "./types";
import { StatusBadge } from "./StatusBadge";
import { StatusIndicator } from "./StatusIndicator";
import { ChevronsUpDown } from "lucide-react";

export function StatusDropdown({ values, options, onChange }: { values: string[]; options: string[]; onChange: (values: string[]) => void }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top,
                left: rect.left,
                width: rect.width,
            });
        }
        setIsOpen(!isOpen);
    };

    const handleToggle = (option: string) => {
        if (option === "All") {
            const allStatuses = STATUS_OPTIONS.filter((opt) => opt !== "All");
            if (values.length === allStatuses.length) {
                onChange([]);
            } else {
                onChange(allStatuses);
            }
        } else {
            if (values.includes(option)) {
                onChange(values.filter((v) => v !== option));
            } else {
                onChange([...values, option]);
            }
        }
    };

    const isAllSelected = values.length === STATUS_OPTIONS.filter((opt) => opt !== "All").length;

    return (
        <div ref={dropdownRef} className="flex items-center justify-between gap-1 z-[9999] cursor-pointer" onClick={handleToggleDropdown}>
            <div ref={buttonRef}>
                <StatusBadge statuses={values} />
            </div>
            <ChevronsUpDown size={14} className="text-[#5A717A]" />

            {isOpen && (
                <div
                    className="fixed font-roboto bg-white border border-gray-200 rounded shadow-lg min-w-[150px] max-h-[250px] overflow-y-auto z-50"
                    style={{
                        top: `${position.top + 30}px`,
                        left: `${position.left}px`,
                        minWidth: `${position.width + 50}px`,
                    }}
                >
                    {options.map((option) => {
                        const isChecked = option === "All" ? isAllSelected : values.includes(option);
                        return (
                            <div
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggle(option);
                                }}
                                className="px-3 py-2 hover:bg-gray-50 text-sm text-title-color flex items-center gap-2"
                            >
                                <div
                                    className={`w-4 h-4 border border-[#ccc] rounded flex items-center justify-center ${
                                        isChecked ? "bg-[#079a95] border-[#079a95]" : "bg-white"
                                    }`}
                                >
                                    {isChecked && (
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                                {option !== "All" && <StatusIndicator status={option} />}
                                <span>{option}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
